import { useState, useEffect } from 'react';
import { getExportTypes, getExports, deleteExport, getExportDownloadUrl, customSqlExport } from '@/api/admin-exports';
import { useAuth } from '@/contexts/AuthContext';
import { tokenStorage } from '@/lib/token';
import type { Export, ExportType } from '@/types/admin-export';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { ExportButton } from '@/components/export/ExportButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  FileSpreadsheet,
  Download,
  Trash2,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  UserCheck,
  GraduationCap,
  Euro,
  Database,
} from 'lucide-react';

// Définition statique des exports avec leurs métadonnées
const V1_EXPORTS = [
  {
    type: 'active-parents',
    name: 'Export des parents actifs',
    description: 'Exporte les parents actifs de toutes les écoles en production.',
    icon: UserCheck,
  },
  {
    type: 'active-directors',
    name: 'Export des membres de la direction',
    description: 'Exporte les membres de la direction actifs de toutes les écoles en production.',
    icon: Users,
  },
  {
    type: 'active-teachers',
    name: 'Export des enseignants actifs',
    description: 'Exporte les enseignants actifs de toutes les écoles en production.',
    icon: GraduationCap,
  },
  {
    type: 'revenue',
    name: 'Export du chiffre d\'affaires',
    description: 'Exporte le chiffre d\'affaires par école et par année fiscale.',
    icon: Euro,
  },
];

export function ExportsPage() {
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole('ROLE_SUPERADMIN');
  const [availableTypes, setAvailableTypes] = useState<ExportType[]>([]);
  const [recentExports, setRecentExports] = useState<Export[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteExportItem, setDeleteExportItem] = useState<Export | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [customSql, setCustomSql] = useState('');
  const [customSqlLoading, setCustomSqlLoading] = useState(false);
  const [customSqlError, setCustomSqlError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [types, exports] = await Promise.all([
        getExportTypes(),
        getExports(),
      ]);
      setAvailableTypes(types);
      setRecentExports(exports);
    } catch (err) {
      setError('Erreur lors du chargement des exports');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteExportItem) return;

    try {
      setDeleting(true);
      await deleteExport(deleteExportItem.id);
      setRecentExports(recentExports.filter((e) => e.id !== deleteExportItem.id));
      setDeleteExportItem(null);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la suppression de l\'export');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async (exportItem: Export) => {
    // Direct download from Azure/storage URL
    if (exportItem.downloadUrl) {
      window.open(exportItem.downloadUrl, '_blank');
      return;
    }

    // Fallback: authenticated download via API
    const url = getExportDownloadUrl(exportItem.id);
    const token = tokenStorage.getAccessToken();

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = exportItem.filePath || 'export.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
      setError('Erreur lors du téléchargement de l\'export');
    }
  };

  const handleCustomSqlExport = async () => {
    const trimmed = customSql.trim();
    if (!trimmed) return;

    if (!/^\s*SELECT\b/i.test(trimmed)) {
      setCustomSqlError('Seules les requêtes SELECT sont autorisées');
      return;
    }

    try {
      setCustomSqlLoading(true);
      setCustomSqlError(null);
      await customSqlExport(trimmed);
    } catch (err: any) {
      const message = err?.response?.data
        ? await new Response(err.response.data).text().then((t) => {
            try { return JSON.parse(t).error || JSON.parse(t).message; } catch { return t; }
          }).catch(() => null)
        : null;
      setCustomSqlError(message || 'Erreur lors de la génération de l\'export');
      console.error(err);
    } finally {
      setCustomSqlLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return '-';
    const units = ['o', 'Ko', 'Mo', 'Go'];
    let unitIndex = 0;
    let size = bytes;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" />
            En attente
          </Badge>
        );
      case 'processing':
        return (
          <Badge variant="secondary" className="gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            En cours
          </Badge>
        );
      case 'completed':
        return null;
      case 'failed':
        return (
          <Badge variant="destructive" className="gap-1">
            <XCircle className="h-3 w-3" />
            Echec
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getExportTypeName = (type: string) => {
    const found = V1_EXPORTS.find((e) => e.type === type);
    if (found) return found.name;
    const apiType = availableTypes.find((t) => t.type === type);
    return apiType?.name || type;
  };

  const isTypeAvailable = (type: string) => {
    return availableTypes.some((t) => t.type === type);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Exports
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Generez et telechargez des exports Excel de vos donnees
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* V1 Exports Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">Exports disponibles</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Ces exports sont generes a partir des donnees de la V1. Une nouvelle version des exports sera disponible prochainement.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          {V1_EXPORTS.map((exportDef) => {
            const Icon = exportDef.icon;
            const available = isTypeAvailable(exportDef.type);

            return (
              <div
                key={exportDef.type}
                className="relative rounded-lg border bg-card p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground">{exportDef.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {exportDef.description}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  {available ? (
                    <ExportButton
                      exportType={exportDef.type}
                      disabled={loading}
                      onExportComplete={fetchData}
                    >
                      Exporter
                    </ExportButton>
                  ) : (
                    <Button variant="outline" disabled>
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      Bientot disponible
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom SQL Export Section - Super-admin only */}
      {isSuperAdmin && (
        <div className="rounded-lg border bg-card p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Export personnalisé</h2>
              <p className="text-sm text-muted-foreground">
                Exécutez une requête SELECT sur toutes les écoles en production
              </p>
            </div>
          </div>

          <Textarea
            placeholder="SELECT ... FROM ..."
            value={customSql}
            onChange={(e) => setCustomSql(e.target.value)}
            rows={5}
            className="font-mono text-sm"
          />

          {customSqlError && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {customSqlError}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleCustomSqlExport}
              disabled={customSqlLoading || !customSql.trim()}
            >
              {customSqlLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Télécharger le résultat
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Recent Exports Section */}
      <div className="rounded-lg border bg-card">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Exports recents</h2>
          <p className="text-sm text-muted-foreground">
            Vos derniers exports generes (conserves 7 jours)
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : recentExports.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <FileSpreadsheet className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Aucun export recent</p>
            <p className="text-sm text-muted-foreground">
              Lancez un export ci-dessus pour le voir apparaitre ici
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {recentExports.map((exportItem) => (
              <div
                key={exportItem.id}
                className="flex items-center gap-4 p-4 hover:bg-muted/50"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                  <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">
                      {getExportTypeName(exportItem.type)}
                    </span>
                    {getStatusBadge(exportItem.status)}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                    <span>{formatDate(exportItem.createdAt)}</span>
                    {exportItem.fileSize && (
                      <span>{formatFileSize(exportItem.fileSize)}</span>
                    )}
                    {exportItem.totalItems && (
                      <span>{exportItem.totalItems} elements</span>
                    )}
                  </div>

                  {/* Progress bar for processing exports */}
                  {exportItem.status === 'processing' && (
                    <div className="mt-2 max-w-xs">
                      <Progress value={exportItem.progress} className="h-2" />
                      <span className="text-xs text-muted-foreground">
                        {exportItem.processedItems}/{exportItem.totalItems} ({exportItem.progress}%)
                      </span>
                    </div>
                  )}

                  {/* Error message */}
                  {exportItem.status === 'failed' && exportItem.errorMessage && (
                    <p className="text-sm text-red-600 mt-1">
                      {exportItem.errorMessage}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {exportItem.status === 'completed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(exportItem)}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Telecharger
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setDeleteExportItem(exportItem)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteExportItem} onOpenChange={() => setDeleteExportItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'export ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer cet export ?
              <br />
              <br />
              Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
